import React, { useCallback } from 'react';
import { ScanEye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import classes from '../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import type { VideoScreenInsights } from '../../../services/api.service';
import VideoInsightsExecutivePanel from './video-insights-executive-panel.component';
import VideoInsightsSourceTable from './video-insights-source-table.component';
import VideoInsightsAccordion from './video-insights-accordion.component';
import {
  buildContextLine,
  extractKeyMetrics,
  isQuoteHighlight,
  sectionAnchorId,
  sectionNavLabel,
} from '../utils/video-insights-utils';

const cn = createCn('file', classes);

type Props = {
  insights: VideoScreenInsights;
  meetingName?: string;
  actionItems?: string[];
};

const MeetingVideoInsightsTab = ({ insights, meetingName, actionItems = [] }: Props) => {
  const { t } = useTranslation();
  const sections = insights.sections || [];
  const glossary = insights.glossary || [];
  const highlights = insights.highlights || [];
  const quoteHighlights = highlights.filter(isQuoteHighlight);
  const keyMetrics = extractKeyMetrics(sections);
  const showNav = sections.length >= 5;

  const scrollToSection = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const hasInfobox = glossary.length > 0 || quoteHighlights.length > 0;

  return (
    <div className={cn('video-insights')}>
      <header className={cn('vi-header')}>
        <h3 className={cn('vi-page-title')}>{t('file.video-insights')}</h3>
        <p className={cn('vi-context-line')}>
          <ScanEye size={13} className={cn('vi-context-icon')} aria-hidden />
          {buildContextLine(meetingName, sections.length)}
        </p>
      </header>

      <VideoInsightsExecutivePanel
        insights={insights}
        actionItems={actionItems}
        onJumpToSection={scrollToSection}
      />

      {showNav && (
        <nav className={cn('vi-toc')} aria-label={t('file.video-insights-toc')}>
          {sections.map((section, idx) => {
            const id = sectionAnchorId(idx, section.title);
            return (
              <button
                key={id}
                type="button"
                className={cn('vi-toc-link')}
                onClick={() => scrollToSection(id)}
              >
                {sectionNavLabel(section.title)}
              </button>
            );
          })}
        </nav>
      )}

      {keyMetrics.length > 0 && (
        <VideoInsightsAccordion
          title={t('file.video-insights-kpi')}
          defaultOpen={false}
          variant="table"
        >
          <div className={cn('vi-kpi-grid')}>
            {keyMetrics.map(metric => (
              <button
                key={metric.id}
                type="button"
                className={cn('vi-kpi-card', { total: metric.kind === 'total' })}
                onClick={() => scrollToSection(metric.sectionId)}
              >
                <span className={cn('vi-kpi-label')}>{metric.label}</span>
                <span className={cn('vi-kpi-value-row')}>
                  <span className={cn('vi-kpi-value')}>{metric.value}</span>
                  <span className={cn('vi-kpi-value-caption')}>{metric.valueCaption}</span>
                </span>
                {metric.deviation && (
                  <span className={cn('vi-kpi-deviation-row')}>
                    <span className={cn('vi-kpi-deviation-caption')}>
                      {metric.deviationCaption || 'к плану'}
                    </span>
                    <span
                      className={cn('vi-kpi-deviation', {
                        [metric.deviationTone || 'neutral']: true,
                      })}
                    >
                      {metric.deviation}
                    </span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </VideoInsightsAccordion>
      )}

      {hasInfobox && (
        <VideoInsightsAccordion
          title={t('file.video-insights-infobox')}
          defaultOpen={false}
          variant="table"
        >
          <aside className={cn('vi-infobox-inner')}>
            {glossary.map((item, idx) => (
              <p key={`${item.term}-${idx}`} className={cn('vi-infobox-line')}>
                <strong>{item.term}</strong>
                {' — '}
                {item.definition}
              </p>
            ))}
            {quoteHighlights.map((line, idx) => (
              <p key={`quote-${idx}`} className={cn('vi-infobox-line', { quote: true })}>
                {line}
              </p>
            ))}
          </aside>
        </VideoInsightsAccordion>
      )}

      <div className={cn('vi-tables')}>
        {sections.map((section, sIdx) => (
          <VideoInsightsSourceTable
            key={`${section.title}-${sIdx}`}
            section={section}
            index={sIdx}
            defaultOpen={false}
          />
        ))}
      </div>

      {insights.generated_at && (
        <p className={cn('video-insights-footer')}>
          {t('file.video-insights-updated')}
          {': '}
          {new Date(insights.generated_at).toLocaleString('ru-RU')}
        </p>
      )}
    </div>
  );
};

export default MeetingVideoInsightsTab;
